import { createSupportWidget, recordEvent } from "../support-widget/index.js";
import {
  VibeSdk,
  VibePopupBlockedError,
  VersionConflictError,
  completePopupCallback,
  type ContentCollection,
  type ContentDocument,
  type LiveUpdateStatus,
  type SemanticServiceStatus,
  type Subscription,
  type VibeProfile,
  type VibeScope,
} from "../sdk";
import { NOTE_TEMPLATE_ID } from "../shared/apps";
import {
  dashboardShellTemplate,
  icon,
  type IdentityView,
} from "../web-shared/ui";
import { createAccountMenu, type AccountMenu } from "../account-menu/index.js";
import { markdown } from "./note-format";

declare const __VIBE_NODE_ORIGIN__: string;

interface NotePayload { body: string }
interface Note {
  entityId: string;
  contentId: string;
  version: number;
  body: string;
  createdAt: string;
  updatedAt: string;
}
interface EditorState {
  entityId: string;
  baseVersion: number;
  draft: string;
  original: string;
  saving: boolean;
  message: string;
  tone: "muted" | "good" | "error";
}
interface ConflictState { entityId: string; localBody: string; remote: Note }
type NotesProfile = VibeProfile & IdentityView & { avatarDataUri?: string | null };


const PAGE_SIZE = 50;
const MAX_BODY_LENGTH = 100_000;
const LIVE_RECONNECT_STATUS_DELAY_MS = 1_500;
const REQUIRED_SCOPES: VibeScope[] = [
  "profile:read",
  `content.read:${NOTE_TEMPLATE_ID}`,
  `content.write:${NOTE_TEMPLATE_ID}`,
  "collections.read",
  "collections.write",
];
const app = document.querySelector<HTMLDivElement>("#app")!;
const toastElement = document.querySelector<HTMLDivElement>("#toast")!;
const sdk = new VibeSdk({ nodeOrigin: __VIBE_NODE_ORIGIN__ });
const supportWidget = createSupportWidget({context:{route:"notes"},actionNames:["navigation.changed"],cloudOrigin: __VIBE_NODE_ORIGIN__, appName: "Vibe Notes", analyticsInfo:true, locale:"en", accent:"#6750a4"});
recordEvent(__VIBE_NODE_ORIGIN__, "page_view");

let profile: NotesProfile | null = null;
let notes: Note[] = [];
let collections: ContentCollection[] = [];
let selectedCollectionId: string | null = null;
let contentSubscription: Subscription | null = null;
let collectionSubscription: Subscription | null = null;
let liveError: string | null = null;
const reconnectingLiveChannels = new Set<"collections" | "content">();
let liveReconnectStatusVisible = false;
let liveReconnectStatusTimer: number | null = null;
let nextOffset = 0;
let hasMore = false;
let loadingMore = false;
let captureOpen = false;
let captureDraft = "";
let captureSaving = false;
let editor: EditorState | null = null;
let conflict: ConflictState | null = null;
let membershipOpenId: string | null = null;
let creatingCollection = false;
let searchQuery = "";
let searchMode: "words" | "meaning" = "words";
let searching = false;
let searchEpoch = 0;
let searchTimer: number | null = null;
let searchResultMode: "lexical" | "hybrid" | "semantic" | null = null;
let searchFallback: string | null = null;
let relatedSourceId: string | null = null;
let semanticStatus: SemanticServiceStatus | null = null;
let accountMenu: AccountMenu | null = null;
let accountActionBusy = false;
let authorizationFallback: { mode: "switch" | "logout"; loginHint?: string } | null = null;

if (completePopupCallback()) {
  app.innerHTML = `<div class="callback">Returning to VibeNotes…</div>`;
} else {
  void startWithRedirectCallback();
}

window.addEventListener("vibe:error", (event) => {
  const error = (event as CustomEvent<unknown>).detail;
  liveError = error instanceof Error ? error.message : "Updates paused.";
  if (profile) renderNotebook();
});

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && profile) void refreshSemanticStatus();
});

async function startWithRedirectCallback(): Promise<void> {
  try {
    await sdk.completeRedirectCallback();
  } catch (error) {
    renderLanding();
    toast(messageOf(error));
    return;
  }
  await start();
}

async function start(): Promise<void> {
  app.innerHTML = `<div class="callback">Opening your notes…</div>`;
  try {
    if (!REQUIRED_SCOPES.every((scope) => sdk.hasToken(scope)) && !await sdk.resume(REQUIRED_SCOPES)) {
      await sdk.connectManyWithRedirect(REQUIRED_SCOPES);
    }
    await openNotebook();
  }
  catch (error) {
    renderLanding();
    toast(messageOf(error));
  }
}

function appHeader(connected = false): string {
  return `<header class="app-header"><div class="brand"><img src="/assets/vibe-notes.png" alt="" /><h1>Notes</h1></div>${connected ? `<button id="new-note" class="primary-action" type="button">${icon("plus", { size: 17 })}New note</button>` : ""}</header>`;
}

function renderLanding(): void {
  accountMenu?.destroy(); accountMenu = null;
  app.innerHTML = `<div class="app-shell"><main class="landing">
    <figure class="landing-showcase"><img src="/assets/vibe-notes-showcase.jpg" width="1024" height="1024" alt="A person arranging colourful notes" decoding="async" fetchpriority="high" /></figure>
    <section class="welcome-copy"><div class="landing-brand"><img src="/assets/vibe-notes.png" alt="" width="44" height="44" /><span>VibeNotes</span></div><div class="eyebrow">Your notes, wherever you are</div><h2>Sign in to your notes.</h2><p>Use your Vibe account to continue. Your password stays with Vibe and is never shared with VibeNotes.</p><button id="connect-main" class="primary-action large" type="button">Try signing in again</button>${authorizationFallback?.mode === "logout" ? `<button id="logout-redirect" class="secondary-action redirect-fallback" type="button">Finish signing out in this tab</button><small>VibeNotes access has been cleared. Continue with Vibe to finish signing out there too.</small>` : authorizationFallback?.mode === "switch" ? `<button id="switch-redirect" class="secondary-action redirect-fallback" type="button">Continue in this tab</button><small>The sign-in window was blocked. Continue here to switch accounts.</small>` : `<small>Vibe securely handles your identity in the background.</small>`}</section>
  </main></div>`;
  document.querySelector<HTMLButtonElement>("#connect-main")!.addEventListener("click", (event) => void connectAndOpen(event.currentTarget as HTMLButtonElement));
  document.querySelector<HTMLButtonElement>("#switch-redirect")?.addEventListener("click", () => { void sdk.switchAccount(REQUIRED_SCOPES, { loginHint: authorizationFallback?.loginHint, redirect: true }).catch(error => toast(messageOf(error))); });
  document.querySelector<HTMLButtonElement>("#logout-redirect")?.addEventListener("click", () => void sdk.logoutFromCloud({ redirect: true }));
}

async function connectAndOpen(button: HTMLButtonElement): Promise<void> {
  setBusy(button, true, "Opening sign-in…");
  try {
    await sdk.connectManyWithRedirect(REQUIRED_SCOPES);
  } catch (error) {
    toast(messageOf(error));
    if (document.contains(button)) setBusy(button, false);
  }
}

async function openNotebook(): Promise<void> {
  authorizationFallback = null;
  profile = await sdk.profile() as NotesProfile;
  await startCollectionSubscription();
  semanticStatus = await sdk.semantic.status({ templateId: NOTE_TEMPLATE_ID }).catch(() => null);
  await startContentSubscription();
}

async function refreshSemanticStatus(): Promise<void> {
  const latest = await sdk.semantic.status({ templateId: NOTE_TEMPLATE_ID }).catch(() => null);
  if (latest && JSON.stringify(latest) !== JSON.stringify(semanticStatus)) {
    semanticStatus = latest;
    renderNotebook();
  }
}

async function startCollectionSubscription(): Promise<void> {
  clearLiveChannel("collections");
  collectionSubscription?.unsubscribe();
  collectionSubscription = await sdk.collections.subscribe({ templateId: NOTE_TEMPLATE_ID }, async (latest) => {
    collections = [...latest].sort((left, right) => left.name.localeCompare(right.name));
    if (selectedCollectionId && !collections.some((collection) => collection.id === selectedCollectionId)) {
      selectedCollectionId = null;
      void startContentSubscription();
      return;
    }
    if (profile) renderNotebook();
  }, (status) => updateLiveChannel("collections", status));
}

async function startContentSubscription(): Promise<void> {
  clearLiveChannel("content");
  contentSubscription?.unsubscribe();
  notes = [];
  nextOffset = 0;
  hasMore = false;
  liveError = null;
  contentSubscription = await sdk.content.subscribe<NotePayload>({
    templateId: NOTE_TEMPLATE_ID,
    collectionId: selectedCollectionId ?? undefined,
    limit: PAGE_SIZE,
  }, async (page) => {
    if (searchQuery || relatedSourceId) {
      if (!searching) void (relatedSourceId ? runRelated(relatedSourceId, false) : runSearch(searchQuery, false));
      return;
    }
    notes = mergeNotes(page.documents.flatMap(noteFromDocument), nextOffset === 0 ? [] : notes);
    if (nextOffset === 0) {
      nextOffset = page.documents.length;
      hasMore = page.hasMore;
    }
    if (!editor && !conflict && !captureSaving) renderNotebook();
  }, (status) => updateLiveChannel("content", status));
}

function updateLiveChannel(channel: "collections" | "content", status: LiveUpdateStatus): void {
  if (status === "reconnecting") reconnectingLiveChannels.add(channel);
  else reconnectingLiveChannels.delete(channel);
  updateLiveReconnectStatus();
}

function clearLiveChannel(channel: "collections" | "content"): void {
  reconnectingLiveChannels.delete(channel);
  updateLiveReconnectStatus();
}

function updateLiveReconnectStatus(): void {
  if (reconnectingLiveChannels.size === 0) {
    if (liveReconnectStatusTimer !== null) window.clearTimeout(liveReconnectStatusTimer);
    liveReconnectStatusTimer = null;
    if (liveReconnectStatusVisible) {
      liveReconnectStatusVisible = false;
      if (profile) renderNotebook();
    }
    return;
  }
  if (liveReconnectStatusVisible || liveReconnectStatusTimer !== null) return;
  liveReconnectStatusTimer = window.setTimeout(() => {
    liveReconnectStatusTimer = null;
    if (reconnectingLiveChannels.size === 0) return;
    liveReconnectStatusVisible = true;
    if (profile) renderNotebook();
  }, LIVE_RECONNECT_STATUS_DELAY_MS);
}

function renderNotebook(): void {
  if (!profile) return;
  const retainedMenu = document.querySelector<HTMLElement>("#notes-account-menu");
  const focusedMenuKey = retainedMenu?.contains(document.activeElement) ? document.activeElement?.getAttribute("data-account-key") : null;
  const selected = collections.find((collection) => collection.id === selectedCollectionId) ?? null;
  const heading = feedHeading(selected?.name ?? null);
  const sidebar = `<aside class="library-nav" aria-label="Notes navigation">
      <div class="notes-sidebar-brand"><img src="/assets/vibe-notes.png" alt="" /><div><strong>VibeNotes</strong><span>Personal notebook</span></div></div>
      <button id="new-note" class="primary-action sidebar-new-note" type="button">${icon("plus", { size: 17 })}New note</button>
      <nav aria-label="Collections">
        <div class="library-heading"><span>Notebook</span></div>
        <button class="library-item ${selected ? "" : "active"}" data-collection="" type="button"><span class="collection-icon">${icon("notebook", { size: 17 })}</span><strong>All notes</strong></button>
        <div class="library-heading collection-heading"><span>Collections</span><button id="new-collection" class="icon-action subtle" type="button" aria-label="New collection">${icon("plus", { size: 17 })}</button></div>
        <div class="collection-list">${collections.map(collectionItem).join("")}</div>
        ${creatingCollection ? `<form id="collection-form" class="collection-form"><input id="collection-name" maxlength="100" autocomplete="off" aria-label="Collection name" placeholder="Collection name" /><div><button id="cancel-collection" class="text-action" type="button">Cancel</button><button class="primary-action compact" type="submit">Create</button></div></form>` : ""}
        ${selected ? `<div class="collection-actions"><button id="rename-collection" class="text-action" type="button">Rename</button><button id="delete-collection" class="text-action danger-text" type="button">Delete</button></div>` : ""}
      </nav>
      <div class="notes-sidebar-footer">${accountControlTemplate()}</div>
    </aside>`;
  const main = `<div class="notes-main">
      <main class="notebook">
        ${liveError ? `<div class="banner">${h(liveError)}</div>` : ""}
        ${searchTemplate()}
        ${searchQuery || relatedSourceId ? "" : captureTemplate()}
        <div class="feed-heading"><div><h2>${h(heading)}</h2><p>${feedSummary()}</p></div><div class="feed-heading-actions">${liveReconnectStatusVisible ? `<div class="live-update-status" role="status">${icon("refresh-cw", { size: 13 })}<span>Reconnecting live updates…</span></div>` : ""}${searchQuery || relatedSourceId ? `<button class="show-all text-action" type="button">Show all</button>` : ""}</div></div>
        <section class="note-feed" aria-label="Notes">${notes.length ? notes.map(noteCard).join("") : emptyTemplate(selected?.name ?? null)}</section>
        <div class="load-more" ${hasMore ? "" : "hidden"}><button id="load-more" class="secondary-action" type="button" ${loadingMore ? "disabled" : ""}>${loadingMore ? "Loading…" : searchQuery || relatedSourceId ? "Load more results" : "Load more notes"}</button></div>
      </main>
    </div>`;
  app.innerHTML = dashboardShellTemplate({ className: "notes-admin-shell", sidebar, main });
  if (retainedMenu) document.querySelector("#notes-account-menu")?.replaceWith(retainedMenu);
  bindNotebookEvents();
  if (focusedMenuKey) document.querySelector<HTMLElement>(`[data-account-key="${focusedMenuKey}"]`)?.focus();
}

function accountControlTemplate(): string {
  return '<div id="notes-account-menu"></div>';
}

function mountAccountMenu(): void {
  const root = document.querySelector<HTMLElement>("#notes-account-menu");
  if (!root || !profile) return;
  const fallback = authorizationFallback;
  const options = {
    appName: "VibeNotes", profile, locale: "en" as const, placement: "above" as const,
    busy: accountActionBusy, remembered: sdk.rememberedAccounts(),
    profileUrl: sdk.nodeOrigin + "/profile",
    onLogin: () => sdk.connectManyWithRedirect(REQUIRED_SCOPES),
    onSwitchAccount: (hint?: string) => switchVibeAccount(hint),
    onLogout: logoutFromNotes,
    onError: (error: unknown) => toast(messageOf(error)),
    notice: fallback?.mode === "switch" ? "Popup blocked. Continue in this tab to sign in." : undefined,
    actions: [
      ...(fallback?.mode === "switch" ? [{ id: "redirect", label: "Continue in this tab", onSelect: async () => { await sdk.switchAccount(REQUIRED_SCOPES, { loginHint: fallback.loginHint, redirect: true }); } }] : []),
      { id: "cloud-logout", label: "Log out of Vibe Cloud too", onSelect: logoutFromVibeCloud },
    ],
  };
  if (accountMenu) accountMenu.update(options);
  else accountMenu = createAccountMenu(root, options);
}

function searchTemplate(): string {
  const active = semanticStatus?.active === true;
  const indexing = active && (semanticStatus?.pending ?? 0) > 0;
  return `<section class="search-panel" aria-label="Search notes"><div class="search-control"><span>${icon("search", { size: 20 })}</span><input id="note-search" type="search" autocomplete="off" value="${h(searchQuery)}" placeholder="Search by words or meaning" aria-label="Search notes" /><button id="clear-search" class="icon-action" type="button" aria-label="Clear search" ${searchQuery ? "" : "hidden"}>${icon("x", { size: 17 })}</button></div><div class="search-options"><div class="search-modes"><button class="search-mode ${searchMode === "words" ? "active" : ""}" data-search-mode="words" type="button">Words</button><button class="search-mode ${searchMode === "meaning" ? "active" : ""}" data-search-mode="meaning" type="button" ${active ? "" : "disabled"}>Meaning</button></div>${active ? `<span>${indexing ? `${semanticStatus!.indexed} indexed · ${semanticStatus!.pending} pending` : "Meaning search ready"}</span>` : `<button id="open-service-setup" class="text-action" type="button">Set up meaning search</button>`}</div></section>`;
}

function collectionItem(collection: ContentCollection): string {
  return `<button class="library-item ${collection.id === selectedCollectionId ? "active" : ""}" data-collection="${h(collection.id)}" type="button"><span class="collection-icon">${icon("folder", { size: 16 })}</span><strong>${h(collection.name)}</strong><em>${collection.entityIds.length}</em></button>`;
}

function captureTemplate(): string {
  if (!captureOpen) return `<button id="capture-prompt" class="capture-prompt" type="button"><span>${icon("plus", { size: 22 })}</span><strong>Write a note…</strong></button>`;
  return `<section class="quick-capture"><textarea id="capture-body" maxlength="${MAX_BODY_LENGTH}" rows="6" aria-label="New note" placeholder="Write a note…">${h(captureDraft)}</textarea><div class="capture-footer"><span>Ctrl+Enter to save · Esc to close</span><div><button id="capture-cancel" class="text-action" type="button">Cancel</button><button id="capture-save" class="primary-action" type="button" ${captureSaving ? "disabled" : ""}>${captureSaving ? "Saving…" : "Save note"}</button></div></div></section>`;
}

function noteCard(note: Note): string {
  if (conflict?.entityId === note.entityId) return conflictCard(conflict);
  if (editor?.entityId === note.entityId) return editorCard(note, editor);
  return `<article class="note-card" data-id="${h(note.entityId)}" tabindex="0"><div class="note-body">${markdown(note.body)}</div><footer class="note-footer"><time datetime="${h(note.updatedAt)}">Updated ${h(relativeTime(note.updatedAt))}</time><div class="card-actions">${semanticStatus?.active ? `<button class="related-note text-action" data-id="${h(note.entityId)}" type="button">Related</button>` : ""}<button class="collections-note text-action" data-id="${h(note.entityId)}" type="button">Collections</button><button class="edit-note text-action" data-id="${h(note.entityId)}" type="button">Edit</button><button class="delete-note text-action danger-text" data-id="${h(note.entityId)}" type="button">Delete</button></div></footer>${membershipOpenId === note.entityId ? membershipMenu(note) : ""}</article>`;
}

function membershipMenu(note: Note): string {
  return `<div class="membership-menu"><strong>Add to collections</strong>${collections.length ? collections.map((collection) => `<label><input class="membership-toggle" type="checkbox" data-id="${h(collection.id)}" ${collection.entityIds.includes(note.entityId) ? "checked" : ""} /><span>${h(collection.name)}</span></label>`).join("") : `<span class="membership-empty">Create a collection in the sidebar first.</span>`}</div>`;
}

function editorCard(note: Note, state: EditorState): string {
  return `<article class="note-card editing" data-id="${h(note.entityId)}"><textarea class="inline-editor" maxlength="${MAX_BODY_LENGTH}" rows="12" aria-label="Edit note" ${state.saving ? "disabled" : ""}>${h(state.draft)}</textarea><div class="editor-footer"><span class="save-state ${state.tone}">${h(state.message)}</span><span>Esc to cancel · Ctrl+Enter to save</span></div></article>`;
}

function conflictCard(state: ConflictState): string {
  return `<article class="note-card conflict-card" data-id="${h(state.entityId)}"><div class="conflict-heading"><div><span class="conflict-badge">Edit conflict</span><h3>Choose what this note should contain</h3></div><p>Both versions are preserved.</p></div><div class="conflict-grid"><section><strong>Your edit</strong><pre>${h(state.localBody)}</pre></section><section><strong>Version on your node</strong><pre>${h(state.remote.body)}</pre></section></div><div class="conflict-actions"><button class="use-remote secondary-action" type="button">Use node version</button><button class="keep-both secondary-action" type="button">Keep both</button><button class="keep-local primary-action" type="button">Keep my edit</button></div></article>`;
}

function bindNotebookEvents(): void {
  mountAccountMenu();
  const searchInput = document.querySelector<HTMLInputElement>("#note-search");
  searchInput?.addEventListener("input", () => {
    searchQuery = searchInput.value;
    relatedSourceId = null;
    if (searchTimer !== null) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => void runSearch(searchQuery, false), 280);
  });
  document.querySelector<HTMLButtonElement>("#clear-search")?.addEventListener("click", () => void resetSearch(true));
  document.querySelectorAll<HTMLButtonElement>(".show-all").forEach((button) => button.addEventListener("click", () => void resetSearch(false)));
  document.querySelector<HTMLButtonElement>("#open-service-setup")?.addEventListener("click", () => {
    window.open(`${sdk.nodeOrigin}/services`, "_blank", "noopener,noreferrer");
  });
  document.querySelectorAll<HTMLButtonElement>(".search-mode").forEach((button) => button.addEventListener("click", () => void selectSearchMode(button)));
  document.querySelectorAll<HTMLButtonElement>(".related-note").forEach((button) => button.addEventListener("click", () => void runRelated(button.dataset.id!, false)));
  document.querySelector<HTMLButtonElement>("#new-note")?.addEventListener("click", openCapture);
  document.querySelector<HTMLButtonElement>("#capture-prompt")?.addEventListener("click", openCapture);
  document.querySelector<HTMLButtonElement>("#empty-new")?.addEventListener("click", openCapture);
  document.querySelectorAll<HTMLButtonElement>(".library-item").forEach((button) => button.addEventListener("click", () => void selectCollection(button.dataset.collection || null)));
  document.querySelector<HTMLButtonElement>("#new-collection")?.addEventListener("click", () => { creatingCollection = true; renderNotebook(); document.querySelector<HTMLInputElement>("#collection-name")?.focus(); });
  document.querySelector<HTMLButtonElement>("#cancel-collection")?.addEventListener("click", () => { creatingCollection = false; renderNotebook(); });
  document.querySelector<HTMLFormElement>("#collection-form")?.addEventListener("submit", (event) => { event.preventDefault(); void createCollection(); });
  document.querySelector<HTMLButtonElement>("#rename-collection")?.addEventListener("click", () => void renameSelectedCollection());
  document.querySelector<HTMLButtonElement>("#delete-collection")?.addEventListener("click", () => void deleteSelectedCollection());
  document.querySelector<HTMLTextAreaElement>("#capture-body")?.addEventListener("input", (event) => { captureDraft = (event.currentTarget as HTMLTextAreaElement).value; });
  document.querySelector<HTMLTextAreaElement>("#capture-body")?.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeCapture();
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) { event.preventDefault(); void saveCapture(); }
  });
  document.querySelector<HTMLButtonElement>("#capture-cancel")?.addEventListener("click", closeCapture);
  document.querySelector<HTMLButtonElement>("#capture-save")?.addEventListener("click", () => void saveCapture());
  document.querySelector<HTMLButtonElement>("#load-more")?.addEventListener("click", () => void loadMore());
  document.querySelectorAll<HTMLButtonElement>(".edit-note").forEach((button) => button.addEventListener("click", () => beginEditing(button.dataset.id!)));
  document.querySelectorAll<HTMLButtonElement>(".delete-note").forEach((button) => button.addEventListener("click", () => void deleteNote(button.dataset.id!)));
  document.querySelectorAll<HTMLButtonElement>(".collections-note").forEach((button) => button.addEventListener("click", () => { membershipOpenId = membershipOpenId === button.dataset.id ? null : button.dataset.id!; renderNotebook(); }));
  document.querySelectorAll<HTMLInputElement>(".membership-toggle").forEach((checkbox) => checkbox.addEventListener("change", () => void toggleMembership(checkbox.dataset.id!, checkbox.checked)));
  const inlineEditor = document.querySelector<HTMLTextAreaElement>(".inline-editor");
  inlineEditor?.addEventListener("input", () => { if (editor) editor.draft = inlineEditor.value; });
  inlineEditor?.addEventListener("keydown", (event) => {
    if (event.key === "Escape") cancelEditing();
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) { event.preventDefault(); void saveEditing(); }
  });
  inlineEditor?.addEventListener("blur", () => void saveEditing());
  document.querySelector<HTMLButtonElement>(".use-remote")?.addEventListener("click", () => resolveConflict("remote"));
  document.querySelector<HTMLButtonElement>(".keep-both")?.addEventListener("click", () => void resolveConflict("both"));
  document.querySelector<HTMLButtonElement>(".keep-local")?.addEventListener("click", () => void resolveConflict("local"));
}

async function switchVibeAccount(loginHint?: string): Promise<void> {
  if (accountActionBusy || !confirmDiscardedDraft()) return;
  // Open authorization in the gesture, then stop rendering the former identity.
  const authorization = sdk.switchAccount(REQUIRED_SCOPES, { loginHint });
  resetNotebookSession();
  accountActionBusy = true;
  app.innerHTML = '<div class="callback" role="status">Finish signing in in the Vibe window…</div>';
  try {
    await authorization;
    // Discard in-flight subscriptions and request closures from the former account.
    // The new SDK session is persisted on this origin before the reload.
    window.location.reload();
  } catch (error) {
    accountActionBusy = false;
    if (error instanceof VibePopupBlockedError) authorizationFallback = { mode: "switch", loginHint };
    renderLanding();
    toast(messageOf(error));
  }
}

async function logoutFromNotes(): Promise<void> {
  if (accountActionBusy || !confirmDiscardedDraft()) return;
  const signingOut = sdk.logout();
  resetNotebookSession();
  accountActionBusy = true;
  app.innerHTML = '<div class="callback" role="status">Signing out…</div>';
  try { await signingOut; }
  catch (error) { toast(messageOf(error)); }
  finally { resetNotebookSession(); renderLanding(); }
}

async function logoutFromVibeCloud(): Promise<void> {
  if (accountActionBusy || !confirmDiscardedDraft()) return;
  accountActionBusy = true;
  renderNotebook();
  try {
    await sdk.logoutFromCloud();
    resetNotebookSession();
    renderLanding();
  } catch (error) {
    const popupBlocked = error instanceof VibePopupBlockedError;
    resetNotebookSession();
    authorizationFallback = popupBlocked ? { mode: "logout" } : null;
    renderLanding();
    toast(popupBlocked
      ? "VibeNotes access is cleared. Continue in this tab to finish logging out of Vibe Cloud."
      : messageOf(error));
  }
}

function confirmDiscardedDraft(): boolean {
  const unsaved = Boolean(captureDraft.trim()) || Boolean(editor && editor.draft !== editor.original);
  return !unsaved || confirm("This account change will discard your unsaved note draft. Continue?");
}

function resetNotebookSession(): void {
  supportWidget.clearContext();
  supportWidget.setContext({route:"notes"});
  contentSubscription?.unsubscribe();
  collectionSubscription?.unsubscribe();
  contentSubscription = null;
  collectionSubscription = null;
  if (searchTimer !== null) window.clearTimeout(searchTimer);
  if (liveReconnectStatusTimer !== null) window.clearTimeout(liveReconnectStatusTimer);
  searchTimer = null;
  liveReconnectStatusTimer = null;
  reconnectingLiveChannels.clear();
  liveReconnectStatusVisible = false;
  profile = null;
  notes = [];
  collections = [];
  selectedCollectionId = null;
  liveError = null;
  nextOffset = 0;
  hasMore = false;
  loadingMore = false;
  captureOpen = false;
  captureDraft = "";
  captureSaving = false;
  editor = null;
  conflict = null;
  membershipOpenId = null;
  creatingCollection = false;
  searchQuery = "";
  searching = false;
  searchEpoch += 1;
  searchResultMode = null;
  searchFallback = null;
  relatedSourceId = null;
  semanticStatus = null;
  accountMenu?.destroy(); accountMenu = null;
  accountActionBusy = false;
  authorizationFallback = null;
}

async function selectSearchMode(button: HTMLButtonElement): Promise<void> {
  const mode = button.dataset.searchMode as "words" | "meaning";
  if (mode === searchMode) return;
  if (mode === "meaning") {
    setBusy(button, true, "Enabling…");
    try {
      await sdk.connectMany([
        `content.read:${NOTE_TEMPLATE_ID}`,
        `semantic.search:${NOTE_TEMPLATE_ID}`,
        "service.use:openai.embeddings",
      ]);
    } catch (error) {
      toast(messageOf(error));
      setBusy(button, false);
      return;
    }
  }
  searchMode = mode;
  if (searchQuery.trim()) await runSearch(searchQuery, false);
  else renderNotebook();
}

async function runSearch(value: string, append: boolean): Promise<void> {
  const query = value.trim();
  if (!query) { await resetSearch(true); return; }
  const epoch = ++searchEpoch;
  searching = true;
  searchQuery = value;
  relatedSourceId = null;
  if (!append) { notes = []; nextOffset = 0; hasMore = false; }
  try {
    const response = searchMode === "meaning"
      ? await sdk.semantic.search<NotePayload>({ templateId: NOTE_TEMPLATE_ID, query, collectionId: selectedCollectionId ?? undefined, offset: append ? nextOffset : 0, limit: PAGE_SIZE })
      : await sdk.semantic.lexical<NotePayload>({ templateId: NOTE_TEMPLATE_ID, query, collectionId: selectedCollectionId ?? undefined, sort: "updated", offset: append ? nextOffset : 0, limit: PAGE_SIZE });
    if (epoch !== searchEpoch) return;
    const pageNotes = response.results.flatMap((result) => noteFromDocument(result.document));
    notes = append ? orderedUnique([...notes, ...pageNotes]) : pageNotes;
    nextOffset = (append ? nextOffset : 0) + pageNotes.length;
    hasMore = response.hasMore;
    searchResultMode = response.mode;
    searchFallback = response.fallbackReason;
    if (semanticStatus) semanticStatus = { ...semanticStatus, indexed: response.indexed, pending: response.pending };
  } catch (error) {
    if (epoch === searchEpoch) toast(messageOf(error));
  } finally {
    if (epoch === searchEpoch) {
      searching = false;
      renderNotebook();
      const input = document.querySelector<HTMLInputElement>("#note-search");
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
    }
  }
}

async function runRelated(entityId: string, append: boolean): Promise<void> {
  const epoch = ++searchEpoch;
  searching = true;
  relatedSourceId = entityId;
  searchQuery = "";
  searchMode = "meaning";
  if (!append) { notes = []; nextOffset = 0; hasMore = false; }
  try {
    const response = await sdk.semantic.related<NotePayload>({
      templateId: NOTE_TEMPLATE_ID,
      entityId,
      collectionId: selectedCollectionId ?? undefined,
      offset: append ? nextOffset : 0,
      limit: PAGE_SIZE,
    });
    if (epoch !== searchEpoch) return;
    const pageNotes = response.results.flatMap((result) => noteFromDocument(result.document));
    notes = append ? orderedUnique([...notes, ...pageNotes]) : pageNotes;
    nextOffset = (append ? nextOffset : 0) + pageNotes.length;
    hasMore = response.hasMore;
    searchResultMode = response.mode;
    searchFallback = response.fallbackReason;
    if (semanticStatus) semanticStatus = { ...semanticStatus, indexed: response.indexed, pending: response.pending };
  } catch (error) {
    if (epoch === searchEpoch) toast(messageOf(error));
  } finally {
    if (epoch === searchEpoch) { searching = false; renderNotebook(); }
  }
}

async function resetSearch(focus: boolean): Promise<void> {
  searchEpoch += 1;
  if (searchTimer !== null) window.clearTimeout(searchTimer);
  searchTimer = null;
  searchQuery = "";
  relatedSourceId = null;
  searchResultMode = null;
  searchFallback = null;
  searching = false;
  await startContentSubscription();
  if (focus) document.querySelector<HTMLInputElement>("#note-search")?.focus();
}

async function selectCollection(collectionId: string | null): Promise<void> {
  if (selectedCollectionId === collectionId) return;
  selectedCollectionId = collectionId;
  editor = null;
  conflict = null;
  membershipOpenId = null;
  await startContentSubscription();
}

async function createCollection(): Promise<void> {
  const input = document.querySelector<HTMLInputElement>("#collection-name");
  const name = input?.value.trim() ?? "";
  if (!name) { input?.focus(); return; }
  try {
    const collection = await sdk.collections.create({ name, templateId: NOTE_TEMPLATE_ID });
    collections = [...collections, collection].sort((left, right) => left.name.localeCompare(right.name));
    creatingCollection = false;
    await selectCollection(collection.id);
  } catch (error) { toast(messageOf(error)); }
}

async function renameSelectedCollection(): Promise<void> {
  const collection = collections.find((entry) => entry.id === selectedCollectionId);
  if (!collection) return;
  const name = window.prompt("Rename collection", collection.name)?.trim();
  if (!name || name === collection.name) return;
  try {
    const updated = await sdk.collections.rename({ collectionId: collection.id, name, version: collection.version });
    replaceCollection(updated);
    renderNotebook();
  } catch (error) { toast(messageOf(error)); }
}

async function deleteSelectedCollection(): Promise<void> {
  const collection = collections.find((entry) => entry.id === selectedCollectionId);
  if (!collection || !window.confirm(`Delete “${collection.name}”? The notes themselves will not be deleted.`)) return;
  try {
    await sdk.collections.delete({ collectionId: collection.id, version: collection.version });
    collections = collections.filter((entry) => entry.id !== collection.id);
    selectedCollectionId = null;
    await startContentSubscription();
  } catch (error) { toast(messageOf(error)); }
}

async function toggleMembership(collectionId: string, included: boolean): Promise<void> {
  const entityId = membershipOpenId;
  const collection = collections.find((entry) => entry.id === collectionId);
  if (!entityId || !collection) return;
  try {
    const updated = await sdk.collections.setMembership({ collectionId, templateId: NOTE_TEMPLATE_ID, entityIds: [entityId], included, version: collection.version });
    replaceCollection(updated);
    if (selectedCollectionId === collectionId && !included) notes = notes.filter((note) => note.entityId !== entityId);
    renderNotebook();
  } catch (error) {
    if (error instanceof VersionConflictError) collections = await sdk.collections.list({ templateId: NOTE_TEMPLATE_ID });
    renderNotebook();
    toast(error instanceof VersionConflictError ? "The collection changed elsewhere. Try again." : messageOf(error));
  }
}

function replaceCollection(updated: ContentCollection): void {
  collections = collections.map((collection) => collection.id === updated.id ? updated : collection).sort((left, right) => left.name.localeCompare(right.name));
}

function openCapture(): void {
  if (searchQuery || relatedSourceId) {
    searchEpoch += 1;
    searchQuery = "";
    relatedSourceId = null;
    searchResultMode = null;
    searchFallback = null;
    void startContentSubscription();
  }
  editor = null; conflict = null; membershipOpenId = null; captureOpen = true; renderNotebook(); focusAtEnd("#capture-body");
}
function closeCapture(): void { if (!captureSaving) { captureOpen = false; captureDraft = ""; renderNotebook(); } }

async function saveCapture(): Promise<void> {
  const body = captureDraft;
  if (!body.trim()) { toast("Write something before saving."); return; }
  const targetCollectionId = selectedCollectionId;
  captureSaving = true;
  renderNotebook();
  let saved: ContentDocument<NotePayload>;
  try {
    saved = await sdk.content.save<NotePayload>({ templateId: NOTE_TEMPLATE_ID, payload: { body }, version: 0 });
  } catch (error) {
    toast(messageOf(error));
    captureSaving = false;
    renderNotebook();
    return;
  }
  try {
    if (targetCollectionId) await addNoteToCollection(saved.entityId, targetCollectionId);
    if (selectedCollectionId === targetCollectionId && saved.payload) notes = mergeNotes([noteFromDocumentValue(saved)], notes);
    toast("Saved.");
  } catch (error) {
    if (!selectedCollectionId && saved.payload) notes = mergeNotes([noteFromDocumentValue(saved)], notes);
    toast(`Note saved in All notes, but it could not be added to the collection: ${messageOf(error)}`);
  } finally {
    captureDraft = "";
    captureOpen = false;
    captureSaving = false;
    renderNotebook();
  }
}

async function addNoteToCollection(entityId: string, collectionId: string): Promise<void> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const collection = collections.find((entry) => entry.id === collectionId);
    if (!collection) throw new Error("The selected collection no longer exists.");
    try {
      replaceCollection(await sdk.collections.setMembership({
        collectionId,
        templateId: NOTE_TEMPLATE_ID,
        entityIds: [entityId],
        included: true,
        version: collection.version,
      }));
      return;
    } catch (error) {
      if (!(error instanceof VersionConflictError) || attempt > 0) throw error;
      collections = await sdk.collections.list({ templateId: NOTE_TEMPLATE_ID });
    }
  }
}

function beginEditing(entityId: string): void {
  const note = notes.find((entry) => entry.entityId === entityId);
  if (!note) return;
  captureOpen = false;
  conflict = null;
  membershipOpenId = null;
  editor = { entityId, baseVersion: note.version, draft: note.body, original: note.body, saving: false, message: "Saves when you leave", tone: "muted" };
  renderNotebook();
  focusAtEnd(".inline-editor");
}
function cancelEditing(): void { if (!editor?.saving) { editor = null; renderNotebook(); } }

async function saveEditing(): Promise<void> {
  const state = editor;
  if (!state || state.saving) return;
  if (state.draft === state.original) { editor = null; renderNotebook(); return; }
  if (!state.draft.trim()) { state.message = "A note cannot be empty."; state.tone = "error"; updateSaveState(state); return; }
  state.saving = true; state.message = "Saving…"; state.tone = "muted"; updateSaveState(state);
  try {
    const saved = await sdk.content.save<NotePayload>({ templateId: NOTE_TEMPLATE_ID, entityId: state.entityId, payload: { body: state.draft }, version: state.baseVersion });
    notes = mergeNotes(saved.payload ? [noteFromDocumentValue(saved)] : [], notes);
    editor = null; toast("Saved."); renderNotebook();
  } catch (error) {
    if (error instanceof VersionConflictError) {
      const remote = await sdk.content.getEntity<NotePayload>({ templateId: NOTE_TEMPLATE_ID, entityId: state.entityId });
      if (!remote.payload) throw new Error("This note was deleted on another device.");
      conflict = { entityId: state.entityId, localBody: state.draft, remote: noteFromDocumentValue(remote) };
      notes = mergeNotes([conflict.remote], notes); editor = null; renderNotebook(); return;
    }
    state.saving = false; state.message = messageOf(error); state.tone = "error"; updateSaveState(state);
  }
}

async function resolveConflict(choice: "remote" | "both" | "local"): Promise<void> {
  const state = conflict;
  if (!state) return;
  if (choice === "remote") { notes = mergeNotes([state.remote], notes); conflict = null; renderNotebook(); return; }
  const body = choice === "local" ? state.localBody : `${state.remote.body.trimEnd()}\n\n---\n\n${state.localBody.trimStart()}`;
  try {
    const saved = await sdk.content.save<NotePayload>({ templateId: NOTE_TEMPLATE_ID, entityId: state.entityId, payload: { body }, version: state.remote.version });
    notes = mergeNotes(saved.payload ? [noteFromDocumentValue(saved)] : [], notes); conflict = null; renderNotebook();
  } catch (error) {
    if (error instanceof VersionConflictError) {
      const latest = await sdk.content.getEntity<NotePayload>({ templateId: NOTE_TEMPLATE_ID, entityId: state.entityId });
      if (latest.payload) conflict = { ...state, remote: noteFromDocumentValue(latest) };
      renderNotebook(); toast("Another version arrived. Review the newest copy.");
    } else toast(messageOf(error));
  }
}

async function deleteNote(entityId: string): Promise<void> {
  const note = notes.find((entry) => entry.entityId === entityId);
  if (!note || !window.confirm("Delete this note? Its revision history remains on your node.")) return;
  try {
    await sdk.content.delete({ templateId: NOTE_TEMPLATE_ID, entityId, version: note.version });
    notes = notes.filter((entry) => entry.entityId !== entityId);
    collections = collections.map((collection) => ({ ...collection, entityIds: collection.entityIds.filter((id) => id !== entityId) }));
    if (editor?.entityId === entityId) editor = null;
    renderNotebook(); toast("Note deleted.");
  } catch (error) { toast(messageOf(error)); }
}

async function loadMore(): Promise<void> {
  if (loadingMore || !hasMore) return;
  loadingMore = true; renderNotebook();
  try {
    if (relatedSourceId) { await runRelated(relatedSourceId, true); return; }
    if (searchQuery) { await runSearch(searchQuery, true); return; }
    const page = await sdk.content.query<NotePayload>({ templateId: NOTE_TEMPLATE_ID, collectionId: selectedCollectionId ?? undefined, offset: nextOffset, limit: PAGE_SIZE });
    notes = mergeNotes(page.documents.flatMap(noteFromDocument), notes);
    nextOffset += page.documents.length; hasMore = page.hasMore;
  } catch (error) { toast(messageOf(error)); }
  finally { loadingMore = false; renderNotebook(); }
}

function noteFromDocument(document: ContentDocument<NotePayload>): Note[] { return document.payload ? [noteFromDocumentValue(document)] : []; }
function noteFromDocumentValue(document: ContentDocument<NotePayload>): Note { return { entityId: document.entityId, contentId: document.id, version: document.version, body: document.payload?.body ?? "", createdAt: document.createdAt, updatedAt: document.updatedAt }; }
function mergeNotes(primary: Note[], secondary: Note[]): Note[] {
  const merged = new Map<string, Note>();
  for (const note of [...primary, ...secondary]) {
    const existing = merged.get(note.entityId);
    if (!existing || note.version > existing.version) merged.set(note.entityId, note);
  }
  return [...merged.values()].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}
function orderedUnique(values: Note[]): Note[] {
  const seen = new Set<string>();
  return values.filter((note) => !seen.has(note.entityId) && Boolean(seen.add(note.entityId)));
}
function feedHeading(collectionName: string | null): string {
  if (relatedSourceId) return "Related notes";
  if (searchQuery) return `Results in ${collectionName ?? "All notes"}`;
  return collectionName ?? "All notes";
}
function feedSummary(): string {
  if (searching) return "Searching…";
  const count = `${notes.length}${hasMore ? "+" : ""} ${notes.length === 1 && !hasMore ? "note" : "notes"}`;
  if (relatedSourceId) return `${count} · meaning matches`;
  if (!searchQuery) return count;
  const mode = searchResultMode === "hybrid" ? "word + meaning matches" : searchResultMode === "semantic" ? "meaning matches" : "word matches";
  const fallback = searchFallback === "providerUnavailable" ? " · OpenAI unavailable, showing words" : searchFallback === "providerNotConfigured" ? " · showing words" : "";
  return `${count} · ${mode}${fallback}`;
}
function emptyTemplate(collectionName: string | null): string {
  if (searchQuery || relatedSourceId) return `<div class="empty-state"><div>${icon("search", { size: 27 })}</div><strong>No matching notes</strong><span>${relatedSourceId ? "Related results will appear as indexing completes." : "Try fewer words, a different phrase, or another search mode."}</span><button class="show-all secondary-action" type="button">Show all notes</button></div>`;
  return `<div class="empty-state"><div>${icon(collectionName ? "folder" : "sparkles", { size: 27 })}</div><strong>${collectionName ? "No notes in this collection" : "No notes yet"}</strong><span>${collectionName ? "Add an existing note from its Collections menu, or write one here." : "Capture the first thought you want to keep."}</span><button id="empty-new" class="secondary-action" type="button">Write a note</button></div>`;
}
function focusAtEnd(selector: string): void { const textarea = document.querySelector<HTMLTextAreaElement>(selector); textarea?.focus(); textarea?.setSelectionRange(textarea.value.length, textarea.value.length); }
function updateSaveState(state: EditorState): void {
  const element = document.querySelector<HTMLElement>(".save-state");
  if (element) { element.textContent = state.message; element.className = `save-state ${state.tone}`; }
  const button = document.querySelector<HTMLButtonElement>(".save-edit");
  if (button) { button.disabled = state.saving; button.textContent = state.saving ? "Saving…" : "Save"; }
}
function setBusy(button: HTMLButtonElement, busy: boolean, label = "Working…"): void { if (!button.dataset.label) button.dataset.label = button.textContent ?? "Continue"; button.disabled = busy; button.textContent = busy ? label : button.dataset.label; }
function toast(message: string): void { toastElement.textContent = message; toastElement.classList.add("show"); window.setTimeout(() => toastElement.classList.remove("show"), 3600); }
function messageOf(error: unknown): string { return error instanceof Error ? error.message : "Something went wrong."; }
function initials(value: string): string {
  const parts = value.trim().split(/\s+/u).filter(Boolean);
  return (parts.length > 1 ? `${parts[0]![0] ?? ""}${parts.at(-1)![0] ?? ""}` : parts[0]?.slice(0, 2) ?? "V").toUpperCase();
}
function relativeTime(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: new Date(iso).getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
}
function h(value: string): string { return value.replace(/[&<>"']/gu, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!); }
